import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Box, Alert, CircularProgress, Typography, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import HealthService, { HealthResponse } from '../services/HealthService';

interface HealthContextType {
  healthData: HealthResponse | null;
  isHealthy: boolean;
  isLoading: boolean;
  error: string | null;
  checkHealth: () => Promise<void>;
}

const HealthContext = createContext<HealthContextType | undefined>(undefined);

interface HealthProviderProps {
  children: ReactNode;
}

export const HealthProvider: React.FC<HealthProviderProps> = ({ children }) => {
  const { t } = useTranslation(['errors', 'common']);
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const [isHealthy, setIsHealthy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkHealth = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const health = await HealthService.checkHealth();
      setHealthData(health);
      setIsHealthy(health.status === 'healthy');
    } catch (err: any) {
      setError(err.message || 'Health check failed');
      setIsHealthy(false);
      setHealthData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const value: HealthContextType = {
    healthData,
    isHealthy,
    isLoading,
    error,
    checkHealth
  };

  // Show loading screen while checking health
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {t('buttons.loading', { ns: 'common' })}
        </Typography>
      </Box>
    );
  }

  // Show error screen if health check fails
  if (!isHealthy || error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3,
          gap: 3
        }}
      >
        <Alert 
          severity="error" 
          sx={{ 
            maxWidth: 600, 
            width: '100%',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('serviceUnavailable')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {error || t('serviceUnavailableMessage')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={checkHealth}
            sx={{ mt: 1 }}
          >
            {t('retryHealthCheck')}
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <HealthContext.Provider value={value}>
      {children}
    </HealthContext.Provider>
  );
};

export const useHealth = (): HealthContextType => {
  const context = useContext(HealthContext);
  if (context === undefined) {
    throw new Error('useHealth must be used within a HealthProvider');
  }
  return context;
};
